import React, { useState } from 'react';
import {
    makeStyles,
    Container,
    Box,
    Typography,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Link,
    Theme
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import MailOutlineIcon from '@material-ui/icons/MailOutline';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        backgroundColor: '#FAFAF8',
        minHeight: '100vh',
        fontFamily: '"DM Sans", sans-serif',
    },
    header: {
        padding: theme.spacing(6, 3, 5),
        textAlign: 'center',
        borderBottom: '1px solid #E8E8E5',
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFAF8 100%)',
    },
    logo: {
        fontFamily: '"Instrument Serif", serif',
        fontSize: '42px',
        color: '#1A1A1A',
    },
    contactCard: {
        padding: theme.spacing(3.5, 4),
        marginBottom: theme.spacing(6),
        display: 'flex',
        alignItems: 'center',
        borderRadius: '12px',
        border: '1px solid #E8E8E5',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
            textAlign: 'center',
        },
    },
    iconCircle: {
        width: '48px',
        height: '48px',
        backgroundColor: '#EFF6FF',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: theme.spacing(2.5),
        [theme.breakpoints.down('xs')]: {
            marginRight: 0,
            marginBottom: theme.spacing(2),
        },
    },
    accordion: {
        marginBottom: theme.spacing(1.5),
        borderRadius: '12px !important',
        border: '1px solid #E8E8E5',
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '&:hover': { borderColor: '#D0D0CC' },
    },
    infoCard: {
        padding: theme.spacing(2.5),
        borderRadius: '12px',
        border: '1px solid #E8E8E5',
        height: '100%',
    },
    label: {
        fontSize: '12px',
        textTransform: 'uppercase',
        letterSpacing: '0.8px',
        color: '#6B6B6B',
        fontWeight: 500,
        marginBottom: theme.spacing(0.5),
    },
    // Reusable style for all blue redirect links
    blueMailLink: {
        color: '#2563EB',
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    }
}));

export const PrivacyContent: React.FC = () => {
    const classes = useStyles();
    const [expanded, setExpanded] = useState<string | boolean>(false);

    const handleChange = (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    // Reusable email component to ensure consistency
    const MailLink = () => (
        <Link href="mailto:nagi@gesain.com" className={classes.blueMailLink}>
            nagi@gesain.com
        </Link>
    );

    const faqData = [
        {
            q: "What is LSDB?",
            a: "LSDB is a mobile application designed to help you manage and access your data efficiently on the go. It provides a streamlined interface for viewing, tracking, and interacting with your information from anywhere."
        },
        {
            q: "What devices and iOS versions are supported?",
            a: "LSDB is compatible with iPhones and iPads running iOS 16.0 or later. We recommend keeping your device updated to the latest iOS version for the best experience and security."
        },
        {
            q: "How do I log in to the app?",
            a: `Open the app and enter your registered username and password on the login screen. If you don't have an account, please contact your organization's administrator to get your credentials set up. If you've forgotten your password, use the "Forgot Password" option on the login screen or contact support.`
        },
        {
            q: "The app is crashing or not loading. What should I do?",
            a: (
                <>
                    Try these steps in order: (1) Force-close the app and reopen it. (2) Check that you have a stable internet connection. (3) Make sure your device is running a supported iOS version. (4) Update to the latest version of LSDB from the App Store. (5) Restart your device. If the issue persists, please contact us at <MailLink /> with a description of the problem and your device model.
                </>
            )
        },
        {
            q: "How do I update the app?",
            a: `Open the App Store on your device, go to your profile, and check for available updates. If an update for LSDB is available, tap "Update." We recommend enabling automatic updates in your device settings to always have the latest features and bug fixes.`
        },
        {
            q: "Is my data secure?",
            a: (
                <>
                    Yes. LSDB uses encrypted connections (HTTPS) for all data transfers between your device and our servers.
                    Your data is stored securely and is only accessible to authorized users. For more details, please refer to our{" "}
                    <Link
                        href="https://www.kiwa.com/en/privacy-statement/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.blueMailLink}
                    >
                        Privacy Policy
                    </Link>.
                </>
            )
        },

        {
            q: "How do I delete my account?",
            a: (
                <>
                   To request account deletion, please email   <MailLink />  from the email address associated with your account. Include "Account Deletion Request" in the subject line. We will process your request and permanently remove your data within 30 days, in compliance with applicable privacy regulations.
                </>
            )
        },
        {
            q: "How can I provide feedback or suggest a feature?",
            a: (
                <>
                    We love hearing from our users! Send your feedback, suggestions, or feature requests to <MailLink />. Your input helps us improve LSDB and prioritize the features that matter most.
                </>
            )
        }
    ];

    return (
        <div className={classes.root}>
            <Box className={classes.header}>
                <Typography className={classes.logo}>LSDB</Typography>
                <Typography variant="body2" style={{ color: '#6B6B6B', letterSpacing: '0.3px' }}>
                    Support & Help Center
                </Typography>
            </Box>

            <Container maxWidth="sm" style={{ paddingTop: '40px', paddingBottom: '80px' }}>
                {/* Contact Card with Redirect */}
                <Paper className={classes.contactCard} elevation={0}>
                    <div className={classes.iconCircle}>
                        <MailOutlineIcon style={{ color: '#2563EB' }} />
                    </div>
                    <Box>
                        <Typography variant="body2" color="textSecondary">Need help? Reach out to us at</Typography>
                        <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                            <MailLink />
                        </Typography>
                    </Box>
                </Paper>

                <Typography variant="h5" style={{ fontFamily: '"Instrument Serif", serif', marginBottom: '24px' }}>
                    Frequently Asked Questions
                </Typography>

                <Box mb={6}>
                    {faqData.map((item, i) => (
                        <Accordion
                            key={i}
                            className={classes.accordion}
                            expanded={expanded === `panel${i}`}
                            onChange={handleChange(`panel${i}`)}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography style={{ fontWeight: 600, fontSize: '15.5px' }}>{item.q}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body2" color="textSecondary" style={{ lineHeight: 1.75 }}>
                                    {item.a}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>

                <Typography variant="h5" style={{ fontFamily: '"Instrument Serif", serif', marginBottom: '24px' }}>
                    App Information
                </Typography>

                <Grid container spacing={2}>
                    {[
                        { label: 'Platform', value: 'iOS (iPhone & iPad)' },
                        { label: 'Requires', value: 'iOS 16.0 or later' },
                        { label: 'Developer', value: 'GESAIN' },
                        { label: 'Support', value: <MailLink /> }
                    ].map((item, i) => (
                        <Grid item xs={12} sm={6} key={i}>
                            <Paper className={classes.infoCard} elevation={0}>
                                <Typography className={classes.label}>{item.label}</Typography>
                                <Typography variant="subtitle2" style={{ fontWeight: 500 }}>{item.value}</Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                <Box mt={6} pt={3} borderTop="1px solid #E8E8E5" textAlign="center">
                    <Link
                        href="https://www.kiwa.com/en/privacy-statement/"
                        target="_blank"
                        rel="noopener noreferrer"
                        color="textSecondary"
                        variant="body2"
                        style={{ textDecoration: 'none' }}
                    >
                        Privacy Policy →
                    </Link>
                </Box>
            </Container>
        </div>
    );
};